import React, { useEffect, useRef, useState } from 'react';
import {
  View, StyleSheet, Text, Animated, Easing, AppState,
} from 'react-native';
import Moment from 'moment';
import { useRoute } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import Colors from '../../../../constants/Colors';
import SectionHeader from '../../../../components/SectionHeader';
import { DateRange } from '../../../../redux/dateRangeSlice';
import GetMetric from '../../../../requests/Sensors/GetMetric';
import Style from '../../../../constants/Style';
import LocationSelector from '../../../../components/Occupancy/LocationSelector';
import ScrollViewStyled from '../../../../components/ScrollViewStyled';
import Switch from '../../../../components/Switch';
import MetricScoreSection from './components/MetricScoreSection';
import {
  AssetInstance, Metric, SensorLabel, TemplateType,
} from '../../../../constants/Types';
import getAssets from '../../../../requests/Assets/GetAssets';
import GetExternalEnvironmentData from '../../../../requests/Sensors/GetExternalEnvironmentData';
import { getAverageFromGraphData } from '../../../../components/TopSection';
import GetMetricHappyScoreData from '../../../../requests/Sensors/GetMetricHappyScoreData';
import AirQualityGraph, { MetricProps } from '../../../../graphs/AirQualityGraph';
import MetricValueCard from './components/MetricValueCard';
import AirQualitySafeLevelsSection from './components/AirQualitySafeLevelsSection';
import MetricSelectionButton from './components/MetricSelectionButton';
import MetricInfoCardSection from './components/MetricInfoCardSection';
import { ChartDataPoint } from '../../../../graphs/axisUtils';

interface AirQualitySectionProps {
  metrics: MetricProps[],
  title: string,
  overallScoreIndex: string,
  locationSelectionShown?: boolean,
  selectedSensor?: string,
  sensorName?: string,
  floorId?: string,
  areaId?: string,
  deepLinkMetric?: number,
}

const animation = (animatedValue: Animated.Value) => Animated.sequence([
  Animated.timing(animatedValue, { duration: 0, useNativeDriver: true, toValue: 0 }),
  Animated.timing(animatedValue, {
    duration: 500, useNativeDriver: true, toValue: 1, easing: Easing.ease,
  }),
]);

const inferSeriesStepMs = (data: { date: string }[], activeRange: DateRange): number => {
  const sortedTimes: number[] = (data ?? [])
    .map((point) => new Date(point?.date).valueOf())
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);
  const differences: number[] = [];
  for (let index = 1; index < sortedTimes.length; index += 1) {
    const diff = sortedTimes[index] - sortedTimes[index - 1];
    if (diff > 0) differences.push(diff);
  }
  if (differences.length > 0) {
    const sortedDiffs: number[] = [...differences].sort((a, b) => a - b);
    const medianDiff: number = sortedDiffs[Math.floor(sortedDiffs.length / 2)] ?? 0;
    const rangeMs: number = Math.max(
      1,
      new Date(activeRange.end).valueOf() - new Date(activeRange.start).valueOf(),
    );
    const minStepToCapSlots: number = Math.ceil(rangeMs / 1999);
    return Math.max(60000, minStepToCapSlots, medianDiff);
  }

  const rangeHours: number = Math.max(
    0,
    Moment(activeRange.end).diff(Moment(activeRange.start), 'hours', true),
  );
  if (rangeHours <= 6) return 15 * 60 * 1000;
  if (rangeHours <= 48) return 60 * 60 * 1000;
  if (rangeHours <= 24 * 14) return 6 * 60 * 60 * 1000;
  return 24 * 60 * 60 * 1000;
};

const padDataToRange = (
  rawData: ChartDataPoint[],
  activeRange: DateRange,
): ChartDataPoint[] => {
  if (!activeRange?.start || !activeRange?.end) {
    return (rawData ?? []).map((point, index: number) => ({ ...point, index }));
  }

  const startMs: number = new Date(activeRange.start).valueOf();
  const endMs: number = new Date(activeRange.end).valueOf();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return (rawData ?? []).map((point, index: number) => ({ ...point, index }));
  }

  const stepMs: number = inferSeriesStepMs(rawData, activeRange);
  const maxSlots: number = Math.min(2000, Math.floor((endMs - startMs) / stepMs) + 1);
  const pointsBySlot = new Map<number, number | null>();

  (rawData ?? []).forEach((point) => {
    const timestamp: number = new Date(point?.date).valueOf();
    if (!Number.isFinite(timestamp)) return;
    const slotIndex: number = Math.round((timestamp - startMs) / stepMs);
    if (slotIndex < 0 || slotIndex >= maxSlots) return;
    const value: number | null = typeof point?.value === 'number' && Number.isFinite(point.value)
      ? point.value : null;
    pointsBySlot.set(slotIndex, value);
  });

  return [...Array(maxSlots).keys()].map((slotIndex: number) => ({
    date: new Date(startMs + (slotIndex * stepMs)).toISOString(),
    value: pointsBySlot.has(slotIndex) ? pointsBySlot.get(slotIndex) : null,
    index: slotIndex,
  }));
};

const AirQualitySection = ({
  metrics, title, overallScoreIndex, locationSelectionShown, selectedSensor, sensorName,
  floorId, areaId, deepLinkMetric = null,
}: AirQualitySectionProps) => {
  const range: DateRange = useSelector((state: { dateRange: DateRange }) => (state.dateRange));
  const compareRange: DateRange = useSelector((state: { dateComparisonRange: DateRange }) => (state
    .dateComparisonRange));
  const liveDateRange = useSelector((state: { liveDateRange }) => (state.liveDateRange));
  const showComparison: boolean = !!compareRange.start && !!compareRange.end;
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const routeName: string = useRoute().name;
  const [defaultShown, setDefaultShown] = useState<boolean>(false);
  const [overallScore, setOverallScore] = useState<number>(null);
  const [overallScoreCompared, setOverallScoreCompared] = useState<number>(null);
  const [selectedMetricIndex, setSelectedMetricIndex] = useState<number>(0);
  const [alternateMetricIndex, setAlternateMetricIndex] = useState<number>(null);
  const [locationName, setLocationName] = useState<string | null>(null);
  const [selectedExternalId, setSelectedExternalId] = useState<string | null>(null);
  const [selectedSensorName, setSelectedSensorName] = useState<string | null>(null);
  const [selectedSensorId, setSelectedSensorId] = useState<string | null>(selectedSensor);
  const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<ChartDataPoint[][]>(metrics.map(() => []));
  const [graphDataCompared,
    setGraphDataCompared] = useState<ChartDataPoint[][]>(metrics.map(() => []));
  const graphRequestIdRef = useRef<number>(0);
  const compareGraphRequestIdRef = useRef<number>(0);
  const [liveValues] = useState<number[]>(metrics.map(() => null));
  const [scores] = useState<number[]>(metrics.map(() => null));
  const [scoresCompared] = useState<number[]>(metrics.map(() => null));
  const [chartSectionOpacity] = useState<Animated.Value>(new Animated.Value(1));
  const [chartOpacity] = useState<Animated.Value>(new Animated.Value(1));
  const [sectionOpacity] = useState<Animated.Value>(new Animated.Value(1));
  const [containerHeight, setContainerHeight] = useState<number>(500);
  const [sensorsShown, setSensorsShown] = useState<boolean>(false);
  const [locationsWithoutSensors, setLocationsWithoutSensors] = useState<boolean>(false);

  const [locationAssets, setLocationAssets] = useState<string[] | null>(null);

  useEffect(() => {
    const selectedLocationId: string = selectedSpaceId ?? selectedAreaId ?? selectedFloorId;
    if (!selectedLocationId) {
      setLocationAssets(null);
      return;
    }
    getAssets({
      locationId: selectedLocationId,
      floorId: selectedFloorId,
      enableSubLocationsLookUp: true,
    }).then((res) => {
      setLocationAssets(res?.assets
        ?.filter((asset: AssetInstance) => asset.type === TemplateType.SENSOR)
        .map((asset: AssetInstance) => asset.id) ?? []);
    }).catch(() => setLocationAssets([]));
  }, [selectedBuildingId, selectedFloorId, selectedAreaId, selectedSpaceId]);

  useEffect(() => {
    setSelectedSensorId(selectedSensor);
    setSelectedSensorName(sensorName);
    setSelectedFloorId(floorId);
    setSelectedAreaId(areaId);
    if (selectedSensor) setSensorsShown(true);
  }, [selectedSensor]);

  useEffect(() => {
    if (deepLinkMetric == null) return;
    if (deepLinkMetric >= 0 && deepLinkMetric < metrics.length) {
      setSelectedMetricIndex(deepLinkMetric);
    }
  }, [deepLinkMetric, metrics.length]);

  const fetchGraphData = async (compare: boolean = false) => {
    if (defaultShown || (compare && !showComparison)) {
      setGraphDataCompared([]);
      return;
    }
    let requestId: number;
    if (compare) {
      compareGraphRequestIdRef.current += 1;
      requestId = compareGraphRequestIdRef.current;
    } else {
      graphRequestIdRef.current += 1;
      requestId = graphRequestIdRef.current;
    }

    const newData: ChartDataPoint[][] = await Promise.all(
      metrics.map(async (metric: MetricProps) => {
        if (metric.index) {
          const metricData = await GetExternalEnvironmentData({
            metric: metric.index,
            field: metric.field,
            range: compare ? compareRange : range,
          });
          const graphPoints: ChartDataPoint[] = metricData
            ?.map((point: { date: string, value: null | number }, index: number) => ({
              ...point, index,
            })) ?? [];
          return padDataToRange(graphPoints, compare ? compareRange : range);
        }

        const metricData = await (metric.table ? GetMetric({
          metric: metric.table,
          range: compare ? compareRange : range,
          externalId: selectedExternalId,
          deviceNumId: selectedSensorId,
          aggregationFunction: metric.aggregationFunction,
        }) : GetMetricHappyScoreData({
          metric: metric.scoreValue,
          range: compare ? compareRange : range,
        }));
        const graphPoints: ChartDataPoint[] = [];
        (metricData ?? []).forEach((e, index: number) => graphPoints.push({
          value: typeof e.value === 'number' ? (e.value / (metric.factor ?? 1)) : null,
          date: e.timestamp ?? e.date,
          index,
        }));
        return padDataToRange(graphPoints, compare ? compareRange : range);
      }),
    );

    if (compare) {
      if (requestId !== compareGraphRequestIdRef.current) return;
      setGraphDataCompared(newData);
      return;
    }

    if (requestId !== graphRequestIdRef.current) return;
    setGraphData(newData);
  };

  useEffect(() => {
    fetchGraphData();
  }, [range, selectedExternalId, selectedSensorId, defaultShown]);
  useEffect(() => {
    fetchGraphData(true);
  }, [
    compareRange, selectedExternalId, selectedSensorId, defaultShown]);

  const fetchLiveData = () => {
    if (!(!defaultShown && AppState.currentState === 'active'
      && (routeName?.toLowerCase().includes('internal')
        || routeName?.toLowerCase().includes('external')))) return;
    metrics.forEach((metric, index) => {
      if (metric.index) {
        GetExternalEnvironmentData({
          metric: metric.index,
          field: metric.field,
          range: liveDateRange,
        })?.then((metricData) => {
          const value: number | null = metricData ? metricData[metricData.length - 1]?.value : null;
          liveValues[index] = (typeof value === 'number' && value > -1)
            ? Math.round(10 * value) / 10 : null;
        });
      } else {
        (metric.table ? GetMetric({
          metric: metric.table,
          range: liveDateRange,
          externalId: selectedExternalId,
          deviceNumId: selectedSensorId,
          aggregationFunction: metric.aggregationFunction,
        }) : GetMetricHappyScoreData({
          metric: metric.scoreValue,
          range: liveDateRange,
        }))?.then((data) => {
          if (!(data?.length > 0)) liveValues[index] = null;
          if (data?.source) {
            liveValues[index] = Math.round(10 * (data.source / (metric.factor ?? 1))) / 10;
          } else {
            const dataPoints = data?.filter((item) => typeof item.value === 'number' && item.value !== null)
              .map((item) => item.value);
            const value: number = dataPoints ? dataPoints[dataPoints.length - 1] : null;
            liveValues[index] = value > -1 ? Math.round(10 * value) / 10 : null;
          }
        });
      }
    });
  };

  useEffect(() => {
    fetchLiveData();
  }, [defaultShown, locationAssets, selectedExternalId, selectedSensorId, liveDateRange]);

  const fetchScoreData = (isCompared: boolean = false) => {
    if (isCompared && !compareRange.end) return;
    metrics.forEach((metric, index: number) => GetMetricHappyScoreData({
      metric: metric.scoreValue,
      range: isCompared ? compareRange : range,
    })?.then((data) => {
      const average: number = getAverageFromGraphData(data);
      const newScore: number = average ? Math.min(100, Math.max(0, Math.floor(average))) : null;
      if (isCompared) scoresCompared[index] = newScore;
      else scores[index] = newScore;
    }));
  };

  const fetchOverallScore = (isComparison: boolean = false) => {
    GetMetricHappyScoreData({
      metric: overallScoreIndex,
      range: isComparison ? compareRange : range,
    }).then((data) => {
      const average: number = getAverageFromGraphData(data);
      if (isComparison) setOverallScoreCompared(average);
      else setOverallScore(average);
    });
  };

  useEffect(() => {
    fetchOverallScore(false);
    fetchScoreData(false);
  }, [range]);

  useEffect(() => {
    fetchOverallScore(true);
    fetchScoreData(true);
  }, [compareRange]);

  useEffect(animation(chartSectionOpacity).start, [selectedMetricIndex, alternateMetricIndex]);
  useEffect(animation(chartOpacity).start, [graphData, graphDataCompared]);
  useEffect(animation(sectionOpacity).start, [defaultShown]);

  const selectMetric = (index: number) => {
    if (!metrics[index]) return;
    if ([selectedMetricIndex, alternateMetricIndex].includes(index)) {
      setSelectedMetricIndex(index);
      setAlternateMetricIndex(null);
      return;
    }
    setAlternateMetricIndex(selectedMetricIndex);
    setSelectedMetricIndex(index);
  };

  const selectedMetric: MetricProps | null = metrics[selectedMetricIndex] ?? null;
  const alternateMetric: MetricProps | null = metrics[alternateMetricIndex] ?? null;
  const selectedMetricData: ChartDataPoint[] = selectedMetric
    ? (graphData[selectedMetricIndex] ?? [])
    : [];
  const selectedMetricComparedData: ChartDataPoint[] = (selectedMetric && showComparison)
    ? (graphDataCompared[selectedMetricIndex] ?? [])
    : [];
  const alternateMetricData: ChartDataPoint[] = alternateMetricIndex == null
    ? null
    : (graphData[alternateMetricIndex] ?? []);
  const alternateMetricComparedData: ChartDataPoint[] = alternateMetricIndex == null
    ? null
    : (graphDataCompared[alternateMetricIndex] ?? []);

  const isMetricVisibleByIndex = (index: number): boolean => {
    const metric = metrics[index];
    if (![
      Metric.NOISE, Metric.LIGHT, Metric.FORMALDEHYDE, Metric.WATER_VAPOUR, Metric.AIR_PRESSURE,
    ].includes(metric?.table as Metric)) return true;

    const series = graphData[index] ?? [];
    if (!series.length) return true;

    return series.some((point: ChartDataPoint) => Number.isFinite(point?.value));
  };

  const visibleMetricEntries: { metric: MetricProps, index: number }[] = metrics
    .map((metric: MetricProps, index: number) => ({ metric, index }))
    .filter(({ index }) => isMetricVisibleByIndex(index));
  const visibleMetrics: MetricProps[] = visibleMetricEntries
    .map(({ metric }) => metric);
  const visibleScores: number[] = visibleMetricEntries
    .map(({ index }) => scores[index]);
  const visibleScoresCompared: number[] = visibleMetricEntries
    .map(({ index }) => scoresCompared[index]);

  useEffect(() => {
    if (!isMetricVisibleByIndex(selectedMetricIndex)) {
      const firstVisibleIndex = visibleMetricEntries[0]?.index;
      if (typeof firstVisibleIndex === 'number') setSelectedMetricIndex(firstVisibleIndex);
    }

    if (alternateMetricIndex != null && !isMetricVisibleByIndex(alternateMetricIndex)) {
      setAlternateMetricIndex(null);
    }
  }, [graphData]);

  useEffect(() => {
    if (!metrics.length) {
      setAlternateMetricIndex(null);
      setSelectedMetricIndex(0);
      return;
    }
    if (!metrics[selectedMetricIndex]) {
      setSelectedMetricIndex(0);
    }
    if (alternateMetricIndex != null && !metrics[alternateMetricIndex]) {
      setAlternateMetricIndex(null);
    }
  }, [metrics, selectedMetricIndex, alternateMetricIndex]);

  return <View>
    <View style={styles.wrapper}>
      {locationSelectionShown && !defaultShown
        && <View style={[styles.locationSelector, { height: containerHeight }]}>
          <LocationSelector
            {...{
              setLocationName,
              setSelectedExternalId,
              selectedBuildingId,
              selectedFloorId,
              setSelectedFloorId,
              selectedAreaId,
              setSelectedAreaId,
              selectedSpaceId,
              setSelectedSpaceId,
              sensorsShown,
              selectedSensorId,
              setSelectedSensorId,
              setSelectedSensorName,
              locationsWithoutSensors,
            }}
            sensorTypesShown={[SensorLabel.MTS, SensorLabel.PCS]}
          />
          <View style={styles.includeSensorsSwitchSection}>
            <Text style={styles.includeSensorsLabel}>{'Include sensors'}</Text>
            <Switch
              toggle={() => {
                if (sensorsShown) {
                  setSelectedSensorId(null);
                  setSelectedSensorName(null);
                }
                setSensorsShown(!sensorsShown);
              }}
              toggled={sensorsShown}
            />
          </View>
          <View style={styles.includeSensorsSwitchSection}>
            <Text style={styles.includeSensorsLabel}>{'Show locations without sensors'}</Text>
            <Switch
              toggle={() => {
                setLocationsWithoutSensors(!locationsWithoutSensors);
              }}
              toggled={locationsWithoutSensors}
            />
          </View>
        </View>}
      <View
        style={styles.container}
        onLayout={({ nativeEvent: { layout: { height } } }) => setContainerHeight(height)}
      >
        <SectionHeader defaultShown={defaultShown}
          setDefaultShown={setDefaultShown}
          button1='INFO'
          button2='GRAPH'
          title1={`${title} parameters`}
          title2={selectedSensorName ?? locationName}
          {...{ range, compareRange, showComparison }}
          style={styles.content}
        />
        {!defaultShown ? <Animated.View style={{ opacity: sectionOpacity }}>
          <ScrollViewStyled horizontal>
            {visibleMetricEntries.map(({ metric, index }) => <MetricValueCard
              key={`${metric.title}${index}_value_card`}
              {...{ metric }}
              value={liveValues[index]}
              count={visibleMetricEntries.length}
            />)}
          </ScrollViewStyled>
          <View style={styles.parametersMainSection}>
            <Animated.View style={{ opacity: chartSectionOpacity }}>
              {selectedMetric ? <>
                <View style={styles.row}>
                  <Text style={styles.metricGraphTitle}>
                    {`${selectedMetric.fullTitle ?? selectedMetric.title}`}
                    {`${selectedMetric.unit ? ` (${selectedMetric.unit})` : ''}`}
                    {!!alternateMetric && <>
                      {' vs. '}
                      {`${alternateMetric.fullTitle ?? alternateMetric.title}`}
                      {`${alternateMetric.unit ? ` (${alternateMetric.unit})` : ''}`}
                    </>}
                  </Text>
                </View>
                <Animated.View style={{ opacity: chartOpacity }}>
                  <AirQualityGraph
                    {...{ showComparison }}
                    data={selectedMetricData}
                    dataCompared={selectedMetricComparedData}
                    title={selectedMetric.title}
                    unit={selectedMetric.unit}
                    type={selectedMetric.type}
                    alternateData={alternateMetricData}
                    alternateDataCompared={alternateMetricComparedData}
                    alternateTitle={alternateMetric?.title}
                    alternateUnit={alternateMetric?.unit}
                    alternateType={alternateMetric?.type}
                  />
                </Animated.View>
                <AirQualitySafeLevelsSection {...{ selectedMetric }} />
              </> : <View style={styles.metricFallbackState}>
                <Text style={styles.metricFallbackText}>
                  {'No metric data available for the current selection.'}
                </Text>
              </View>}
            </Animated.View>
          </View>
          <View style={styles.metricButtonsRow}>
            {visibleMetricEntries.map(({ metric: item, index }) => <MetricSelectionButton
              title={item.title}
              unit={item.unit}
              key={`${item.title}${index}_selection_button`}
              selected={selectedMetricIndex === index}
              alternateSelected={alternateMetricIndex === index}
              onPress={() => selectMetric(index)}
              {...{ index }}
            />)}
          </View>
        </Animated.View>
          : <Animated.View style={[styles.scoreContent, { opacity: sectionOpacity }]}>
            <MetricScoreSection
              {...{
                overallScore,
                overallScoreCompared,
                showComparison,
              }}
              scores={visibleScores}
              scoresCompared={visibleScoresCompared}
              metrics={visibleMetrics}
            />
            <MetricInfoCardSection metrics={visibleMetrics} />
          </Animated.View>}
      </View>
    </View>
  </View>;
};

const styles = StyleSheet.create({
  title: {
    fontSize: 22,
    lineHeight: 33,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  wrapper: {
    ...Style.row,
    backgroundColor: '#FFF',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite300,
    marginVertical: 24,
    overflow: 'visible',
  },
  locationSelector: {
    height: '100%',
    width: 240,
    borderEndWidth: 1,
    borderEndColor: Colors.light.hereworksBlue50,
    alignSelf: 'stretch',
  },
  container: {
    flex: 1,
    paddingTop: 18,
    paddingBottom: 27,
  },
  content: {
    marginBottom: 16,
  },
  scoreContent: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    height: 550,
  },
  metricButtonsRow: {
    flexDirection: 'row',
    marginHorizontal: 18,
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  metricGraphTitle: {
    fontSize: 18,
    lineHeight: 27,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginEnd: 24,
  },
  metricFallbackState: {
    minHeight: 291,
    alignItems: 'center',
    justifyContent: 'center',
  },
  metricFallbackText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack500,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  parametersMainSection: {
    marginVertical: 16,
    marginHorizontal: 24,
    borderBottomWidth: 1.5,
    borderBottomColor: Colors.light.hereworksWhite500,
  },
  includeSensorsSwitchSection: {
    ...Style.row,
    paddingVertical: 16,
    paddingHorizontal: 24,
    backgroundColor: Colors.light.hereworksWhite100,
  },
  includeSensorsLabel: {
    fontSize: 12,
    lineHeight: 12,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack500,
  },
});

export default AirQualitySection;
